import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowSquareOut,
  ArrowUp,
  CaretDown,
  CircleNotch,
  MagnifyingGlass,
  Plus,
  Trash,
  X,
} from '@phosphor-icons/react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import {
  generateWebsitePage,
  getWebsite,
  getWebsiteDesign,
  getWebsiteVersion,
  listWebsiteVersions,
  revertWebsiteVersion,
  setDesignFeaturedTiles,
  setDesignLanguages,
  setDesignModules,
  setDesignPartners,
  setDesignSiteEntries,
  setDesignSlug,
  setDesignTeams,
  setDesignTheme,
  setDesignContentTranslations,
  suggestAlt,
  websitePreviewURL,
  listCustomDomains,
  connectCustomDomain,
  verifyCustomDomain,
  disconnectCustomDomain,
  type Block,
  type BlockType,
  type CustomDomain,
  type RecommendedRecord,
  type PageContent,
  type PageContentTranslation,
  type CuratedFont,
  type FeaturedTile,
  type FeaturedTileKind,
  type Partner,
  type SeedEvent,
  type SeedContentTranslation,
  type SiteEntry,
  type SiteEntryKind,
  type SystemRoute,
  type ThemeOverrides,
  type Website,
  type WebsiteDesign,
  type WebsiteModule,
  type WebsiteVersion,
  type WebsiteVersionDetail,
  FEATURED_TILE_KINDS,
  MAX_FEATURED_TILES,
  SITE_ENTRY_KINDS,
  SYSTEM_ROUTES,
  BLOCK_TYPES,
  WEBSITE_MODULES,
} from '../api/websites';
import { listTeams, updateTeam, type Team } from '../api/teams';
import { ApiError } from '../api/client';
import { getOrgGeneral, type OrgGeneralInfo } from '../api/orgs';
import { config } from '../config';
import { AutosaveStatusBadge } from '../components/AutosaveStatus';
import { ImageInput } from '../components/ImageInput';
import { PageLoader } from '../components/PageLoader';
import { useConfirm } from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { WebsitePreviewDrawer } from '../components/WebsitePreviewDrawer';
import { useManualSave } from '../hooks/useManualSave';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { TablePagination } from '../components/ui/table-pagination';
import { LanguagesField, PLATFORM_LANGUAGES } from '../components/LanguagesField';

// WebsiteDesignPage is the opt-in design-mode editor at
// /orgs/:slug/website/:websiteId/design.
//
// Layout: left-hand tabbed panel (mode toggle / nav / pages / theme),
// right-hand live preview iframe. Each tab's Save button PUTs to the
// matching /design/* endpoint; the iframe reloads on success so the
// preview reflects the change without a full page refresh.
//
// "Inline design mode" here means: the editor lives next to the preview,
// not literally overlaid on it. True click-on-the-iframe editing
// requires a postMessage bridge between the preview and the SPA, which
// is a meaningful separate scope; this layout still gives you live
// feedback for every edit.

// Tabs were consolidated: 'settings' folds the former general + theme +
// domain panels into one (rendered as stacked sections), and 'structure'
// folds the former nav + pages panels. The old values are gone; the
// preview-iframe deep-links remap onto the merged tabs below.
type Tab = 'settings' | 'modules' | 'structure' | 'teams' | 'featured' | 'partners' | 'translations' | 'history';

// Initial drawer width on first open — matches the historical tablet
// preset (WebsitePreviewDrawer owns the actual drag/clamp logic).
const INITIAL_DRAWER_WIDTH = 860;

// normalizeHex coerces a free-text input to a 6-digit hex string for
// <input type="color">, which insists on exactly that shape. Falls back
// to the provided default when the input doesn't parse (empty, 3-digit
// shorthand we'd need to expand, garbage). Only used for the picker's
// initial value — the text input next to it carries the canonical value.
function normalizeHex(input: string | undefined, fallback: string): string {
  if (!input) return fallback;
  const m = /^#([0-9a-fA-F]{6})$/.exec(input);
  if (m) return `#${m[1]!.toLowerCase()}`;
  const s = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/.exec(input);
  if (s) return `#${s[1]!}${s[1]!}${s[2]!}${s[2]!}${s[3]!}${s[3]!}`.toLowerCase();
  return fallback;
}

export function WebsiteDesignPage() {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language?.slice(0, 2) || 'en';
  const auth = useAuth();
  const navigate = useNavigate();
  const { slug = '', websiteId = '' } = useParams<{ slug: string; websiteId: string }>();
  const token = auth.user?.access_token ?? null;

  const [loading, setLoading] = useState(true);
  const [bootstrapErr, setBootstrapErr] = useState<string | null>(null);
  const [site, setSite] = useState<Website | null>(null);
  const [design, setDesign] = useState<WebsiteDesign | null>(null);
  // orgGeneral carries the org-level branding fallback (primary / accent
  // colors set in the wizard). When the per-website theme_overrides are
  // empty, the ThemePanel surfaces these as the rendered defaults so the
  // color picker reflects what visitors actually see.
  const [orgGeneral, setOrgGeneral] = useState<OrgGeneralInfo | null>(null);
  const [tab, setTab] = useState<Tab>('settings');

  // Preview drawer state. Open/close + width live here so the editor
  // body can reserve padding-right that tracks the drawer; device preset
  // and drag-resize behaviour are owned by WebsitePreviewDrawer.
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerWidth, setDrawerWidth] = useState<number>(INITIAL_DRAWER_WIDTH);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // previewTick is appended to the iframe URL as a cache-buster param.
  // Incrementing it changes the URL so React rewrites the iframe's src,
  // which the browser treats as a fresh navigation — more reliable than
  // the older "set src='' then back" trick, which is racy if the iframe
  // is mid-load when we touch it.
  const [previewTick, setPreviewTick] = useState(0);
  const refreshPreview = useCallback(() => {
    setPreviewTick((n) => n + 1);
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    setBootstrapErr(null);
    try {
      const [w, d, g] = await Promise.all([
        getWebsite(token, slug, websiteId),
        getWebsiteDesign(token, slug, websiteId, locale),
        getOrgGeneral(token, slug).catch(() => null),
      ]);
      setSite(w);
      setDesign(d);
      setOrgGeneral(g);
    } catch (err) {
      setBootstrapErr((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token, slug, websiteId, locale]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const reloadDesign = useCallback(async () => {
    const d = await getWebsiteDesign(token, slug, websiteId, locale);
    setDesign(d);
    refreshPreview();
  }, [token, slug, websiteId, locale, refreshPreview]);

  if (loading || !site || !design) {
    return (
      <div className="website-design website-design--loading">
        {loading && <PageLoader />}
        {bootstrapErr && <p className="website-design__error">{bootstrapErr}</p>}
      </div>
    );
  }

  // Iframe URL: the preview iframe is a passive browseable view of the
  // org's public site. The admin edits via the side panels, not the
  // iframe — clicking links in the iframe navigates the iframe inline,
  // letting the user check the live result of their changes.
  const previewURL = site.preview_token
    ? `${websitePreviewURL(config.apiURL, config.publicSitesBaseDomain, site.slug, site.preview_token)}&_t=${previewTick}`
    : '';

  // Body padding tracks the drawer's width so the editor's content
  // doesn't slide underneath the fixed drawer on the right. CSS reads
  // --design-drawer-width and pads body accordingly; setting it to 0
  // collapses the gap when the drawer is closed.
  const rootStyle = {
    '--design-drawer-width': drawerOpen ? `${drawerWidth}px` : '0px',
  } as React.CSSProperties;

  return (
    <div className={`website-design${drawerOpen ? ' website-design--drawer-open' : ''}`} style={rootStyle}>
      <header className="website-design__header">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => navigate(`/orgs/${encodeURIComponent(slug)}/website`)}
        >
          {t('website.design.backToList')}
        </button>
        <h1>{t('website.design.title')}</h1>
        <p className="website-design__subtitle">{t('website.design.subtitle')}</p>
        <button
          type="button"
          className="btn btn--ghost website-design__preview-toggle"
          onClick={() => setDrawerOpen((o) => !o)}
          aria-expanded={drawerOpen}
        >
          {drawerOpen ? t('website.design.previewHide') : t('website.design.previewShow')}
        </button>
      </header>

      <div className="website-design__body">
        <nav className="website-design__tabs" role="tablist">
          {/* 'settings' merges general + theme + domain; 'structure'
              merges nav + pages. Each renders the original panels as
              stacked sections in the panel area below. */}
          <TabButton current={tab} value="settings" onClick={setTab} label={t('website.design.tabs.general')} />
          <TabButton current={tab} value="modules" onClick={setTab} label={t('website.design.tabs.modules')} />
          <TabButton current={tab} value="structure" onClick={setTab} label={t('website.design.tabs.structure')} />
          <TabButton current={tab} value="teams" onClick={setTab} label={t('website.design.tabs.teams')} />
          {/* Featured tiles are a magazine-template-only feature — the
              row of spotlight cards beside the hero. Other templates
              ignore the payload, so the tab only appears for magazine. */}
          {site.template === 'magazine' && (
            <TabButton current={tab} value="featured" onClick={setTab} label={t('website.design.tabs.featured')} />
          )}
          <TabButton current={tab} value="partners" onClick={setTab} label={t('website.design.tabs.partners')} />
          {/* Translations only make sense once a site offers more than one
              language — the tab edits per-language variants of the page copy. */}
          {design.supported_languages.length > 1 && (
            <TabButton current={tab} value="translations" onClick={setTab} label={t('website.design.tabs.translations')} />
          )}
          <TabButton current={tab} value="history" onClick={setTab} label={t('website.design.tabs.history')} />
        </nav>

        <div className="website-design__panel">
          {tab === 'settings' && (
            <>
              <DesignSection title={t('website.design.tabs.general')}>
                <GeneralPanel
                  design={design}
                  onSaveSlug={async (newSlug) => {
                    await setDesignSlug(token, slug, websiteId, newSlug);
                    await reloadDesign();
                  }}
                />
              </DesignSection>
              <DesignSection title={t('website.design.tabs.theme')}>
                <ThemePanel
                  design={design}
                  orgGeneral={orgGeneral}
                  onSave={async (theme) => {
                    await setDesignTheme(token, slug, websiteId, theme);
                    await reloadDesign();
                  }}
                />
              </DesignSection>
              <DesignSection title={t('website.wizard.languages.heading')}>
                <LanguagesPanel
                  design={design}
                  onSave={async (supported, primary) => {
                    await setDesignLanguages(token, slug, websiteId, supported, primary);
                    await reloadDesign();
                  }}
                />
              </DesignSection>
              <DesignSection title={t('website.design.tabs.domain', { defaultValue: 'Domain' })}>
                <DomainPanel token={token} slug={slug} websiteId={websiteId} />
              </DesignSection>
            </>
          )}
          {tab === 'structure' && (
            <SiteEntriesPanel
              design={design}
              onSave={async (entries) => {
                await setDesignSiteEntries(token, slug, websiteId, entries);
                await reloadDesign();
              }}
              onGenerate={(brief) =>
                generateWebsitePage(token, slug, websiteId, brief, locale)
              }
            />
          )}
          {tab === 'teams' && (
            <TeamsPanel
              token={token}
              slug={slug}
              design={design}
              onSave={async (teamIDs) => {
                await setDesignTeams(token, slug, websiteId, teamIDs);
                await reloadDesign();
              }}
            />
          )}
          {tab === 'featured' && site.template === 'magazine' && (
            <FeaturedPanel
              design={design}
              onSave={async (tiles) => {
                await setDesignFeaturedTiles(token, slug, websiteId, tiles);
                await reloadDesign();
              }}
            />
          )}
          {tab === 'modules' && (
            <ModulesPanel
              design={design}
              onSave={async (modules) => {
                await setDesignModules(token, slug, websiteId, modules);
                await reloadDesign();
              }}
            />
          )}
          {tab === 'partners' && (
            <PartnersPanel
              design={design}
              onSave={async (partners) => {
                await setDesignPartners(token, slug, websiteId, partners);
                await reloadDesign();
              }}
            />
          )}
          {tab === 'translations' && (
            <TranslationsPanel
              design={design}
              onSave={async (sourceLanguage, translations) => {
                await setDesignContentTranslations(token, slug, websiteId, sourceLanguage, translations);
                await reloadDesign();
              }}
            />
          )}
          {tab === 'history' && (
            <HistoryPanel
              token={token}
              slug={slug}
              websiteId={websiteId}
              onAfterRevert={reloadDesign}
            />
          )}
        </div>
      </div>

      <WebsitePreviewDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={drawerWidth}
        onWidthChange={setDrawerWidth}
        previewURL={previewURL}
        iframeRef={iframeRef}
      />
    </div>
  );
}

// DesignSection wraps one former tab's panel inside a merged tab,
// giving it a heading + divider so the stacked sections read as
// distinct groups (e.g. "Allmänt" / "Tema" / "Domän" under one tab).
function DesignSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="website-design__section">
      <h2 className="website-design__section-title">{title}</h2>
      {children}
    </section>
  );
}

function TabButton({
  current,
  value,
  onClick,
  label,
}: {
  current: Tab;
  value: Tab;
  onClick: (t: Tab) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={current === value}
      className={`website-design__tab ${current === value ? 'website-design__tab--active' : ''}`}
      onClick={() => onClick(value)}
    >
      {label}
    </button>
  );
}

// SaveFooter renders the "Save changes" button + status badge every
// panel surfaces. Disabled when nothing's pending so a stray click can't
// trigger a no-op write (which would otherwise add an empty version row
// to history). The status badge already lives in the panel header next
// to the title; this footer is the place the user clicks to commit.
function SaveFooter({
  save,
}: {
  save: { status: 'idle' | 'pending' | 'saved' | 'failed'; dirty: boolean; commit: () => Promise<void> };
}) {
  const { t } = useTranslation('common');
  const busy = save.status === 'pending';
  return (
    <div className="design-panel__save-footer">
      <button
        type="button"
        className="btn btn--primary"
        disabled={!save.dirty || busy}
        onClick={() => void save.commit()}
      >
        {busy ? t('common.submitting') : t('common.save')}
      </button>
    </div>
  );
}

// --- Mode toggle -----------------------------------------------------

// --- Modules toggles -------------------------------------------------

// ModulesPanel lets the admin toggle which built-in sections (news,
// schedule, teams, …) render on the public site without going back
// through the wizard. The wizard sets the initial list; this panel is
// the post-wizard editor. Same audit + version-snapshot path as the
// other design setters so toggles show up in History.
function ModulesPanel({
  design,
  onSave,
}: {
  design: WebsiteDesign;
  onSave: (modules: WebsiteModule[]) => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const [enabled, setEnabled] = useState<Set<WebsiteModule>>(
    () => new Set(design.enabled_modules),
  );
  // Memoize a stable array view of the set so the dirty check sees a
  // deterministic shape. Order matches WEBSITE_MODULES so a toggle-then-
  // toggle-back lands back on the same JSON the snapshot captured.
  const enabledList = useMemo<WebsiteModule[]>(
    () => WEBSITE_MODULES.filter((m) => enabled.has(m)),
    [enabled],
  );
  const initialList = useMemo<WebsiteModule[]>(
    () => WEBSITE_MODULES.filter((m) => design.enabled_modules.includes(m)),
    [design.enabled_modules],
  );
  const save = useManualSave(enabledList, onSave, initialList);

  const toggle = (m: WebsiteModule) => {
    setEnabled((curr) => {
      const next = new Set(curr);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
  };

  return (
    <section className="design-panel">
      <header className="design-panel__head">
        <h2>{t('website.design.modules.heading')}</h2>
        <AutosaveStatusBadge status={save.status} error={save.error} />
      </header>
      <p>{t('website.design.modules.body')}</p>
      <ul className="design-panel__modules">
        {WEBSITE_MODULES.map((m) => (
          <li key={m} className="design-panel__module">
            <label className="design-panel__module-label">
              <input
                type="checkbox"
                checked={enabled.has(m)}
                onChange={() => toggle(m)}
              />
              <span className="design-panel__module-text">
                <span className="design-panel__module-name">
                  {t(`website.design.modules.module.${m}.label`)}
                </span>
                <span className="design-panel__module-body">
                  {t(`website.design.modules.module.${m}.body`)}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <SaveFooter save={save} />
    </section>
  );
}

// LanguagesPanel edits an existing site's multilingual config from the
// settings tab — which platform languages the site offers and which is
// primary. Mirrors the wizard's create-time picker via the shared
// LanguagesField; saves through the /design/languages endpoint so the
// change shows up in History like every other design edit.
function LanguagesPanel({
  design,
  onSave,
}: {
  design: WebsiteDesign;
  onSave: (supported: string[], primary: string) => Promise<void>;
}) {
  const [supported, setSupported] = useState<string[]>(design.supported_languages);
  const [primary, setPrimary] = useState<string>(design.primary_language);
  const value = useMemo(() => ({ supported, primary }), [supported, primary]);
  const initial = useMemo(
    () => ({ supported: design.supported_languages, primary: design.primary_language }),
    [design.supported_languages, design.primary_language],
  );
  const save = useManualSave(value, (v) => onSave(v.supported, v.primary), initial);

  return (
    <section className="design-panel">
      {/* No <h2> here: the enclosing DesignSection already titles this
          "Språk", and LanguagesField's legend is suppressed (hideHeading),
          so the badge-only head avoids three stacked "Språk" headings. */}
      <header className="design-panel__head design-panel__head--end">
        <AutosaveStatusBadge status={save.status} error={save.error} />
      </header>
      <LanguagesField
        hideHeading
        supported={supported}
        primary={primary}
        onChange={({ supported: s, primary: p }) => {
          setSupported(s);
          setPrimary(p);
        }}
      />
      <SaveFooter save={save} />
    </section>
  );
}

// --- Site entries editor ---------------------------------------------

// newEntryID returns a fresh stable id for a new entry. crypto.randomUUID
// is present in every browser that runs this app; the type guard is a
// no-op safety net for very old WebViews where the API would be missing.
function newEntryID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `e-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

// slugifyPageTitle turns a free-text title into a candidate slug for
// new kind=page entries. Mirrors the backend regex (lowercase a-z, 0-9,
// hyphen). Returns "" when nothing maps — the editor surfaces the
// validation error instead of silently writing a junk slug.
function slugifyPageTitle(input: string): string {
  return input
    .toLowerCase()
    .replace(/å/g, 'a').replace(/ä/g, 'a').replace(/ö/g, 'o')
    .replace(/ø/g, 'o').replace(/æ/g, 'ae').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

// SiteEntriesPanel renders the unified Sidor & navigering editor. One
// ordered list, each row is a SiteEntry (system route / custom page /
// external link). Pages expand inline into the block editor. Reorder
// arrows + show-in-nav checkbox per row. "Use automatic" empties the
// list, which the renderer treats as the auto-nav fallback.
function SiteEntriesPanel({
  design,
  onSave,
  onGenerate,
}: {
  design: WebsiteDesign;
  onSave: (entries: SiteEntry[]) => Promise<void>;
  // onGenerate is set when the AI is configured server-side. When
  // undefined the "Generate with AI" button is hidden — same nil-safe
  // contract as suggestAlt's 503 path.
  onGenerate: ((brief: string) => Promise<PageContent>) | null;
}) {
  const { t } = useTranslation('common');
  const [entries, setEntries] = useState<SiteEntry[]>(design.site_entries);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiErr, setAiErr] = useState<string | null>(null);
  const confirm = useConfirm();
  const rowRefs = useRef<(HTMLInputElement | null)[]>([]);
  const save = useManualSave(entries, onSave, design.site_entries);

  const pageSlugs = useMemo(
    () =>
      entries
        .filter((e) => e.kind === 'page' && e.page)
        .map((e) => e.page!.slug)
        .filter((s) => s !== ''),
    [entries],
  );

  const update = (idx: number, patch: Partial<SiteEntry>) => {
    setEntries((curr) => curr.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };
  const updatePage = (idx: number, patch: Partial<PageContent>) => {
    setEntries((curr) =>
      curr.map((e, i) => {
        if (i !== idx || e.kind !== 'page' || !e.page) return e;
        return { ...e, page: { ...e.page, ...patch } };
      }),
    );
  };
  const move = (idx: number, dir: -1 | 1) => {
    setEntries((curr) => {
      const next = curr.slice();
      const dest = idx + dir;
      if (dest < 0 || dest >= next.length) return curr;
      const a = next[idx]!;
      const b = next[dest]!;
      next[idx] = b;
      next[dest] = a;
      return next;
    });
    // Keep the expand-state pinned to the same entry it pointed at.
    setExpandedIdx((curr) => {
      if (curr === null) return curr;
      if (curr === idx) return idx + dir;
      if (curr === idx + dir) return idx;
      return curr;
    });
  };
  const remove = async (idx: number) => {
    const e = entries[idx];
    if (!e) return;
    const destructive = e.kind === 'page';
    if (destructive) {
      if (!(await confirm({
        title: t('website.design.siteEntries.removeTitle'),
        body: t('website.design.siteEntries.removeBody'),
        tone: 'destructive',
      }))) {
        return;
      }
    }
    setEntries((curr) => curr.filter((_, i) => i !== idx));
    setExpandedIdx(null);
  };
  const addSystem = () => {
    setEntries((curr) => [
      ...curr,
      {
        id: newEntryID(),
        label: '',
        kind: 'system',
        system_route: 'home',
        show_in_nav: true,
      },
    ]);
  };
  const addPage = () => {
    setEntries((curr) => {
      const next: SiteEntry[] = [
        ...curr,
        {
          id: newEntryID(),
          label: '',
          kind: 'page',
          page: { slug: '', title: '', blocks: [] },
          show_in_nav: true,
        },
      ];
      setExpandedIdx(next.length - 1);
      return next;
    });
  };
  const addExternal = () => {
    setEntries((curr) => [
      ...curr,
      {
        id: newEntryID(),
        label: '',
        kind: 'external',
        external_url: '',
        show_in_nav: true,
      },
    ]);
  };
  const reset = () => {
    setEntries([]);
    setExpandedIdx(null);
  };
  const changeKind = (idx: number, nextKind: SiteEntryKind) => {
    setEntries((curr) =>
      curr.map((e, i) => {
        if (i !== idx) return e;
        const base: SiteEntry = {
          id: e.id,
          label: e.label,
          kind: nextKind,
          show_in_nav: e.show_in_nav,
        };
        if (nextKind === 'system') return { ...base, system_route: 'home' };
        if (nextKind === 'page') return { ...base, page: e.page ?? { slug: '', title: '', blocks: [] } };
        if (nextKind === 'external') return { ...base, external_url: '' };
        return base;
      }),
    );
  };

  return (
    <section className="design-panel">
      <header className="design-panel__head">
        <h2>{t('website.design.siteEntries.heading')}</h2>
        <AutosaveStatusBadge status={save.status} error={save.error} />
      </header>
      <p>{t('website.design.siteEntries.body')}</p>
      {design.entries_are_auto && entries.length > 0 && (
        <p className="design-panel__hint">{t('website.design.siteEntries.autoHint')}</p>
      )}

      {entries.length === 0 ? (
        <p className="design-panel__empty">{t('website.design.siteEntries.empty')}</p>
      ) : (
        <ol className="flex flex-col gap-2 list-none m-0 p-0">
          {entries.map((e, idx) => {
            const isExpanded = expandedIdx === idx && e.kind === 'page';
            const kindVariant: 'default' | 'secondary' | 'outline' =
              e.kind === 'page' ? 'default' : e.kind === 'external' ? 'outline' : 'secondary';
            return (
              <li
                key={e.id}
                className="rounded-lg border border-border bg-card p-2.5 transition-colors"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={kindVariant} className="shrink-0">
                    {t(`website.design.siteEntries.kind.${e.kind}`)}
                  </Badge>
                  <Input
                    ref={(el) => { rowRefs.current[idx] = el; }}
                    type="text"
                    aria-label={t('website.design.siteEntries.labelField')}
                    value={e.label}
                    maxLength={40}
                    placeholder={t('website.design.siteEntries.labelPlaceholder')}
                    onChange={(ev) => update(idx, { label: ev.target.value })}
                    className="flex-1 min-w-[10rem]"
                  />
                  <Select
                    value={e.kind}
                    onValueChange={(v) => changeKind(idx, v as SiteEntryKind)}
                  >
                    <SelectTrigger
                      aria-label={t('website.design.siteEntries.kindField')}
                      className="w-[10rem]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SITE_ENTRY_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {t(`website.design.siteEntries.kind.${k}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {e.kind === 'system' && (
                    <Select
                      value={e.system_route ?? 'home'}
                      onValueChange={(v) =>
                        update(idx, { system_route: v as SystemRoute })
                      }
                    >
                      <SelectTrigger
                        aria-label={t('website.design.siteEntries.systemRoute')}
                        className="w-[10rem]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SYSTEM_ROUTES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {t(`website.design.siteEntries.systemRouteLabel.${r}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {e.kind === 'external' && (
                    <Input
                      type="url"
                      placeholder="https://…"
                      value={e.external_url ?? ''}
                      onChange={(ev) => update(idx, { external_url: ev.target.value })}
                      className="flex-1 min-w-[10rem]"
                    />
                  )}
                  {e.kind === 'page' && (
                    <code className="rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground/70">
                      /p/{e.page?.slug || '?'}
                    </code>
                  )}
                  <label className="flex items-center gap-2 text-sm text-foreground/80 cursor-pointer select-none">
                    <Checkbox
                      checked={e.show_in_nav}
                      onChange={(ev) =>
                        update(idx, { show_in_nav: (ev.target as HTMLInputElement).checked })
                      }
                    />
                    <span>{t('website.design.siteEntries.showInNav')}</span>
                  </label>
                  <div className="ml-auto flex items-center gap-1">
                    {e.kind === 'page' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-expanded={isExpanded}
                        aria-label={
                          isExpanded
                            ? t('website.design.siteEntries.collapsePage')
                            : t('website.design.siteEntries.expandPage')
                        }
                        onClick={() => {
                          setExpandedIdx((curr) => (curr === idx ? null : idx));
                        }}
                      >
                        <CaretDown
                          weight="bold"
                          className={cn('transition-transform', isExpanded && 'rotate-180')}
                        />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      aria-label={t('common.up')}
                    >
                      <ArrowUp weight="bold" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => move(idx, 1)}
                      disabled={idx === entries.length - 1}
                      aria-label={t('common.down')}
                    >
                      <ArrowDown weight="bold" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => void remove(idx)}
                      aria-label={t('common.remove')}
                      className="text-foreground/70 hover:text-destructive"
                    >
                      <Trash weight="bold" />
                    </Button>
                  </div>
                </div>
                {isExpanded && e.kind === 'page' && e.page && (
                  <PageEditor
                    page={e.page}
                    stockUrls={design.stock_image_urls}
                    supportedLanguages={design.supported_languages}
                    primaryLanguage={design.primary_language}
                    onChange={(patch) => updatePage(idx, patch)}
                    onClose={() => {
                      setExpandedIdx(null);
                    }}
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}

      <div className="design-panel__actions">
        <button type="button" className="btn btn--ghost" onClick={addSystem}>
          {t('website.design.siteEntries.addSystem')}
        </button>
        <button type="button" className="btn btn--ghost" onClick={addPage}>
          {t('website.design.siteEntries.addPage')}
        </button>
        <button type="button" className="btn btn--ghost" onClick={addExternal}>
          {t('website.design.siteEntries.addExternal')}
        </button>
        {onGenerate && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setAiErr(null);
              setAiOpen(true);
            }}
          >
            {t('website.design.siteEntries.generateCta')}
          </button>
        )}
        <button type="button" className="btn btn--ghost" onClick={reset}>
          {t('website.design.siteEntries.resetAuto')}
        </button>
      </div>

      {onGenerate && (
        <GeneratePageDialog
          open={aiOpen}
          error={aiErr}
          onCancel={() => {
            setAiOpen(false);
            setAiErr(null);
          }}
          onGenerate={async (brief) => {
            setAiErr(null);
            try {
              const page = await onGenerate(brief);
              // Wrap the model's PageContent reply as a kind=page entry
              // and open its inline editor so the admin can tweak before
              // saving. Generation is dismissable without an explicit
              // revert — nothing's persisted yet.
              setEntries((curr) => {
                const fallbackSlug = page.slug || slugifyPageTitle(page.title);
                const next: SiteEntry[] = [
                  ...curr,
                  {
                    id: newEntryID(),
                    label: page.title,
                    kind: 'page',
                    page: { ...page, slug: fallbackSlug },
                    show_in_nav: true,
                  },
                ];
                setExpandedIdx(next.length - 1);
                return next;
              });
              setAiOpen(false);
            } catch (e) {
              setAiErr((e as Error).message || 'generate_failed');
            }
          }}
        />
      )}
      {/* pageSlugs is exported so future helpers (e.g. preview deep-link)
          can reference admin-defined slugs without re-deriving them. */}
      <input type="hidden" data-page-slugs={pageSlugs.join(',')} />
      <SaveFooter save={save} />
    </section>
  );
}

// GeneratePageDialog is the small "describe a page, AI drafts it" modal
// surfaced by the Pages tab when the AI is configured server-side. The
// brief is free-form text; on submit the dialog calls onGenerate which
// is the parent's bound API client. The dialog stays open while the
// promise pends so the busy state is visible.
function GeneratePageDialog({
  open,
  error,
  onCancel,
  onGenerate,
}: {
  open: boolean;
  error: string | null;
  onCancel: () => void;
  onGenerate: (brief: string) => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setBrief('');
      setBusy(false);
    }
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="schedule-dialog"
      onClose={onCancel}
      onClick={(e) => {
        if (e.target === dialogRef.current) onCancel();
      }}
    >
      <div className="schedule-dialog__inner" onClick={(e) => e.stopPropagation()}>
        <h3>{t('website.design.siteEntries.generateTitle')}</h3>
        <p>{t('website.design.siteEntries.generateBody')}</p>
        <textarea
          rows={5}
          value={brief}
          maxLength={2000}
          placeholder={t('website.design.siteEntries.generatePlaceholder')}
          onChange={(e) => setBrief(e.target.value)}
        />
        {error && <p className="schedule-dialog__error" role="alert">{error}</p>}
        <footer className="schedule-dialog__foot">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || brief.trim().length === 0}
            onClick={async () => {
              setBusy(true);
              try {
                await onGenerate(brief);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? t('common.submitting') : t('website.design.siteEntries.generateConfirm')}
          </button>
        </footer>
      </div>
    </dialog>
  );
}

// SuggestAltButton hits the alt-text endpoint for one storage key
// and calls onSuggestion with the result. Hides itself when the
// backend reports 503 (AI not configured) so non-AI deployments
// don't render a useless button. Idle / busy / failed states are
// reflected on the label.
function SuggestAltButton({
  objectKey,
  onSuggestion,
}: {
  objectKey: string;
  onSuggestion: (s: { alt: string; caption: string }) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const { slug = '', websiteId = '' } = useParams<{ slug: string; websiteId: string }>();
  const [state, setState] = useState<'idle' | 'busy' | 'unavailable' | 'failed'>('idle');

  if (state === 'unavailable') return null;

  return (
    <button
      type="button"
      className="btn btn--ghost design-panel__suggest"
      disabled={state === 'busy'}
      onClick={async () => {
        setState('busy');
        try {
          const result = await suggestAlt(
            auth.user?.access_token ?? null,
            slug,
            websiteId,
            objectKey,
            i18n.language,
          );
          onSuggestion(result);
          setState('idle');
        } catch (e) {
          if (e instanceof ApiError && e.status === 503) {
            setState('unavailable');
          } else {
            setState('failed');
          }
        }
      }}
    >
      {state === 'busy'
        ? t('common.submitting')
        : state === 'failed'
          ? t('website.design.blocks.suggestRetry')
          : t('website.design.blocks.suggestAlt')}
    </button>
  );
}

// GalleryEditor renders one ImageInput per slot plus a single empty
// "+" slot so adding an image is a single click instead of a textarea
// edit. Up to 24 images (matches the server's maxGalleryImages).
function GalleryEditor({
  imageKeys,
  onChange,
}: {
  imageKeys: string[];
  onChange: (keys: string[]) => void;
}) {
  const { t } = useTranslation('common');
  const max = 24;
  const set = (i: number, key: string) => {
    const next = imageKeys.slice();
    if (key) {
      next[i] = key;
    } else {
      next.splice(i, 1);
    }
    onChange(next);
  };
  const add = (key: string) => {
    if (!key) return;
    onChange([...imageKeys, key]);
  };
  return (
    <div className="gallery-editor">
      {imageKeys.map((k, i) => (
        <ImageInput
          key={`${i}-${k}`}
          value={k}
          variant="compact"
          onChange={(next) => set(i, next)}
        />
      ))}
      {imageKeys.length < max && (
        <ImageInput
          key={`empty-${imageKeys.length}`}
          value=""
          variant="compact"
          onChange={add}
          label={t('website.design.blocks.galleryAdd')}
        />
      )}
    </div>
  );
}

function PageEditor({
  page,
  stockUrls,
  supportedLanguages,
  primaryLanguage,
  onChange,
  onClose,
}: {
  page: PageContent;
  // Map of "stock.<kind>[.<n>]" → resolved CDN URL. Passed down so the
  // image block's ImageInput can render previews for AI-generated stock
  // references (otherwise the empty preview reads as "broken").
  stockUrls?: Record<string, string>;
  supportedLanguages: string[];
  primaryLanguage: string;
  onChange: (patch: Partial<PageContent>) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');

  const setBlock = (idx: number, patch: Partial<Block>) => {
    onChange({ blocks: page.blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b)) });
  };
  const moveBlock = (idx: number, dir: -1 | 1) => {
    const next = page.blocks.slice();
    const dest = idx + dir;
    if (dest < 0 || dest >= next.length) return;
    const a = next[idx]!;
    const b = next[dest]!;
    next[idx] = b;
    next[dest] = a;
    onChange({ blocks: next });
  };
  const removeBlock = (idx: number) => onChange({ blocks: page.blocks.filter((_, i) => i !== idx) });
  const addBlock = (type: BlockType) => {
    const fresh: Block = { type };
    onChange({ blocks: [...page.blocks, fresh] });
  };

  return (
    <div className="page-editor">
      <div className="page-editor__meta">
        <label>
          {t('website.design.siteEntries.pageSlug')}
          <input
            type="text"
            value={page.slug}
            onChange={(e) => onChange({ slug: e.target.value })}
            placeholder="om-oss"
          />
        </label>
        <label>
          {t('website.design.siteEntries.pageTitle')}
          <input
            type="text"
            value={page.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder={t('website.design.siteEntries.pageTitlePlaceholder')}
          />
        </label>
      </div>

      <ol className="page-editor__blocks">
        {page.blocks.map((b, idx) => (
          <li
            key={idx}
            className="page-editor__block"
          >
            <header>
              <span className="page-editor__block-type">{t(`website.design.blocks.${b.type}`)}</span>
              <Select
                value={String(b.columns ?? 1)}
                onValueChange={(v) =>
                  setBlock(idx, { columns: Number(v) as 1 | 2 | 3 })
                }
              >
                <SelectTrigger
                  aria-label={t('website.design.blocks.widthLabel')}
                  className="!min-h-8 !w-auto !min-w-[7rem]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('website.design.blocks.widthFull')}</SelectItem>
                  <SelectItem value="2">{t('website.design.blocks.widthHalf')}</SelectItem>
                  <SelectItem value="3">{t('website.design.blocks.widthThird')}</SelectItem>
                </SelectContent>
              </Select>
              {/* Vertical alignment only matters in a multi-column row —
                  on full-width blocks it has no visible effect, so hide
                  the control to keep the header tidy. */}
              {b.columns && b.columns > 1 ? (
                <Select
                  value={b.alignSelf ?? 'top'}
                  onValueChange={(v) =>
                    // Store every value, including the default "top",
                    // so a patch-merge can actually flip from middle
                    // back to top. (Stripping the field would leave
                    // the old value in the merged block.) Server-side
                    // validation accepts "top" as a no-op alongside
                    // empty.
                    setBlock(idx, { alignSelf: v as 'top' | 'middle' | 'bottom' })
                  }
                >
                  <SelectTrigger
                    aria-label={t('website.design.blocks.alignLabel')}
                    className="!min-h-8 !w-auto !min-w-[7rem]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">{t('website.design.blocks.alignTop')}</SelectItem>
                    <SelectItem value="middle">{t('website.design.blocks.alignMiddle')}</SelectItem>
                    <SelectItem value="bottom">{t('website.design.blocks.alignBottom')}</SelectItem>
                  </SelectContent>
                </Select>
              ) : null}
              <button type="button" onClick={() => moveBlock(idx, -1)} aria-label={t('common.up')}>↑</button>
              <button type="button" onClick={() => moveBlock(idx, 1)} aria-label={t('common.down')}>↓</button>
              <button type="button" onClick={() => removeBlock(idx)} aria-label={t('common.remove')}>×</button>
            </header>
            {(b.type === 'heading' || b.type === 'paragraph') && (
              <textarea
                rows={b.type === 'heading' ? 2 : 5}
                value={b.text ?? ''}
                maxLength={8000}
                onChange={(e) => setBlock(idx, { text: e.target.value })}
              />
            )}
            {b.type === 'button' && (
              <>
                <input
                  type="text"
                  placeholder={t('website.design.blocks.buttonText')}
                  value={b.text ?? ''}
                  onChange={(e) => setBlock(idx, { text: e.target.value })}
                />
                <input
                  type="url"
                  placeholder="https://… or /c/…"
                  value={b.href ?? ''}
                  onChange={(e) => setBlock(idx, { href: e.target.value })}
                />
              </>
            )}
            {b.type === 'image' && (
              <>
                <ImageInput
                  value={b.imageKey ?? ''}
                  {...(stockUrls ? { stockUrls } : {})}
                  onChange={(key) => setBlock(idx, { imageKey: key })}
                />
                <input
                  type="text"
                  placeholder={t('website.design.blocks.caption')}
                  value={b.caption ?? ''}
                  onChange={(e) => setBlock(idx, { caption: e.target.value })}
                />
                {/* SuggestAltButton hits the storage-keyed alt-text
                    endpoint — it can't analyse a stock photo we don't
                    have an object_key for. Hide it for stock refs. */}
                {b.imageKey && !b.imageKey.startsWith('stock.') && (
                  <SuggestAltButton
                    objectKey={b.imageKey}
                    onSuggestion={(s) => {
                      // Caption serves dual duty as the rendered <img alt>.
                      // Prefer the model's caption; fall back to alt when
                      // it's empty (decorative or short-description case).
                      const next = s.caption || s.alt;
                      if (next) setBlock(idx, { caption: next });
                    }}
                  />
                )}
              </>
            )}
            {b.type === 'gallery' && (
              <GalleryEditor
                imageKeys={b.imageKeys ?? []}
                onChange={(keys) => setBlock(idx, { imageKeys: keys })}
              />
            )}
            {b.type === 'embed' && (
              <input
                type="url"
                placeholder="https://www.youtube.com/embed/…"
                value={b.src ?? ''}
                onChange={(e) => setBlock(idx, { src: e.target.value })}
              />
            )}
          </li>
        ))}
      </ol>

      <div className="page-editor__add">
        {BLOCK_TYPES.map((type) => (
          <button key={type} type="button" className="btn btn--ghost" onClick={() => addBlock(type)}>
            + {t(`website.design.blocks.${type}`)}
          </button>
        ))}
      </div>

      {supportedLanguages.length > 1 && (
        <PageTranslationsEditor
          page={page}
          supportedLanguages={supportedLanguages}
          primaryLanguage={primaryLanguage}
          onChange={onChange}
        />
      )}

      <button type="button" className="btn btn--ghost" onClick={onClose}>
        {t('common.done')}
      </button>
    </div>
  );
}

// blockText returns the translatable text of a block (heading/paragraph/button
// text, image caption) or null when the block carries nothing to translate
// (gallery images, embeds). Drives which block rows the translation editor
// shows and how a translated block is rebuilt.
function blockText(b: Block): string | null {
  if (b.type === 'heading' || b.type === 'paragraph' || b.type === 'button') return b.text ?? '';
  if (b.type === 'image') return b.caption ?? '';
  return null;
}

function setBlockText(b: Block, text: string): Block {
  if (b.type === 'image') return { ...b, caption: text };
  return { ...b, text };
}

// PageTranslationsEditor edits the per-language variants of a custom page's
// title + block text. The source page (the controlled `page` prop) is the
// reference; each target language gets a title input and one input per
// translatable source block. Blank fields fall back to the source at render.
// A language's translated blocks publish as a complete mirror of the source
// (structure + images preserved, text overridden) once any block text is
// translated — matching the renderer's wholesale block swap. Edits push the
// rebuilt translations through the page's onChange, so they ride the same
// site-entries save + pending→publish flow as every other page edit.
function PageTranslationsEditor({
  page,
  supportedLanguages,
  primaryLanguage,
  onChange,
}: {
  page: PageContent;
  supportedLanguages: string[];
  primaryLanguage: string;
  onChange: (patch: Partial<PageContent>) => void;
}) {
  const { t } = useTranslation('common');
  const sourceLang = page.source_language || primaryLanguage;
  const targetLangs = useMemo(
    () => supportedLanguages.filter((c) => c !== sourceLang),
    [supportedLanguages, sourceLang],
  );

  type LangDraft = { title: string; texts: string[] };

  // Draft state initialized once from the stored translations; each text row
  // mirrors a source block by index. We track user input separately from the
  // source so an untranslated row stays blank (and falls back) rather than
  // echoing the source text.
  const initialDrafts = useMemo<Record<string, LangDraft>>(() => {
    const out: Record<string, LangDraft> = {};
    for (const code of targetLangs) {
      const tr = page.translations?.[code];
      out[code] = {
        title: tr?.title ?? '',
        texts: page.blocks.map((_, i) => {
          const tb = tr?.blocks?.[i];
          return tb ? (blockText(tb) ?? '') : '';
        }),
      };
    }
    return out;
    // Initialize once per page identity; subsequent onChange-driven page
    // updates must not clobber in-progress drafts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetLangs.join(','), page.blocks.length]);

  const [drafts, setDrafts] = useState<Record<string, LangDraft>>(initialDrafts);
  useEffect(() => setDrafts(initialDrafts), [initialDrafts]);

  const langName = (code: string) =>
    PLATFORM_LANGUAGES.find((l) => l.code === code)?.endonym ?? code.toUpperCase();

  // Rebuild the canonical translations map and push it through onChange.
  const commit = (next: Record<string, LangDraft>) => {
    const translations: Record<string, PageContentTranslation> = {};
    for (const code of targetLangs) {
      const d = next[code];
      if (!d) continue;
      const title = d.title.trim();
      const anyText = page.blocks.some(
        (b, i) => blockText(b) !== null && (d.texts[i] ?? '').trim() !== '',
      );
      let blocks: Block[] | undefined;
      if (anyText) {
        blocks = page.blocks.map((b, i) => {
          if (blockText(b) === null) return { ...b };
          const tx = (d.texts[i] ?? '').trim();
          return setBlockText(b, tx || (blockText(b) ?? ''));
        });
      }
      if (title === '' && !blocks) continue;
      const entry: PageContentTranslation = {};
      if (title !== '') entry.title = title;
      if (blocks) entry.blocks = blocks;
      translations[code] = entry;
    }
    onChange({ source_language: sourceLang, translations });
  };

  const updateTitle = (code: string, title: string) => {
    const next = { ...drafts, [code]: { ...drafts[code]!, title } };
    setDrafts(next);
    commit(next);
  };
  const updateText = (code: string, idx: number, text: string) => {
    const texts = drafts[code]!.texts.slice();
    texts[idx] = text;
    const next = { ...drafts, [code]: { ...drafts[code]!, texts } };
    setDrafts(next);
    commit(next);
  };

  return (
    <div className="page-editor__translations">
      <h4>{t('website.design.translations.heading')}</h4>
      <p className="design-panel__hint">{t('website.design.pageTranslations.body')}</p>
      {targetLangs.map((code) => {
        const d = drafts[code]!;
        return (
          <fieldset key={code} className="design-panel__translations-lang" lang={code}>
            <legend>{langName(code)}</legend>
            <label className="design-panel__field">
              <span className="design-panel__field-label">
                {t('website.design.siteEntries.pageTitle')}
              </span>
              <Input
                type="text"
                value={d.title}
                placeholder={page.title}
                onChange={(e) => updateTitle(code, e.target.value)}
              />
            </label>
            {page.blocks.map((b, i) => {
              const src = blockText(b);
              if (src === null) return null;
              return (
                <label key={i} className="design-panel__field">
                  <span className="design-panel__field-label">
                    {t(`website.design.blocks.${b.type}`)}
                  </span>
                  <textarea
                    className="design-panel__textarea"
                    rows={b.type === 'paragraph' ? 3 : 2}
                    value={d.texts[i] ?? ''}
                    placeholder={src}
                    onChange={(e) => updateText(code, i, e.target.value)}
                  />
                </label>
              );
            })}
          </fieldset>
        );
      })}
    </div>
  );
}

// --- Theme editor ----------------------------------------------------

function ThemePanel({
  design,
  orgGeneral,
  onSave,
}: {
  design: WebsiteDesign;
  orgGeneral: OrgGeneralInfo | null;
  onSave: (theme: ThemeOverrides) => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const [theme, setTheme] = useState<ThemeOverrides>(design.theme_overrides);
  const fonts = design.curated_fonts;
  const headingFonts = fonts.filter((f: CuratedFont) => f.category === 'heading' || f.category === 'both');
  const bodyFonts = fonts.filter((f: CuratedFont) => f.category === 'body' || f.category === 'both');
  const save = useManualSave(theme, onSave, design.theme_overrides);

  // Org-level color defaults — what the public site renders with when no
  // per-website override is set. Surface these as the color picker's
  // fallback so the picker reflects the live color, not a hard-coded
  // lime green that doesn't match what the visitor sees.
  const orgPrimary = orgGeneral?.primary_color || '#BEE034';
  const orgAccent = orgGeneral?.accent_color || '#26272B';

  return (
    <section className="design-panel">
      <header className="design-panel__head">
        <h2>{t('website.design.theme.heading')}</h2>
        <AutosaveStatusBadge status={save.status} error={save.error} />
      </header>
      <p>{t('website.design.theme.body')}</p>

      <label className="design-panel__field design-panel__field--color">
        {t('website.design.theme.primaryColor')}
        <span className="design-panel__color-row">
          <input
            type="color"
            value={normalizeHex(theme.primaryColor, orgPrimary)}
            onChange={(e) => setTheme((s) => ({ ...s, primaryColor: e.target.value }))}
          />
          <input
            type="text"
            value={theme.primaryColor ?? ''}
            placeholder={orgPrimary}
            maxLength={7}
            onChange={(e) => setTheme((s) => ({ ...s, primaryColor: e.target.value }))}
          />
        </span>
      </label>
      <label className="design-panel__field design-panel__field--color">
        {t('website.design.theme.accentColor')}
        <span className="design-panel__color-row">
          <input
            type="color"
            value={normalizeHex(theme.accentColor, orgAccent)}
            onChange={(e) => setTheme((s) => ({ ...s, accentColor: e.target.value }))}
          />
          <input
            type="text"
            value={theme.accentColor ?? ''}
            placeholder={orgAccent}
            maxLength={7}
            onChange={(e) => setTheme((s) => ({ ...s, accentColor: e.target.value }))}
          />
        </span>
      </label>
      <label className="design-panel__field">
        {t('website.design.theme.headingFont')}
        <Select
          value={theme.headingFont ? theme.headingFont : '__default__'}
          onValueChange={(v) =>
            setTheme((s) => {
              const { headingFont: _drop, ...rest } = s;
              return v && v !== '__default__' ? { ...rest, headingFont: v } : rest;
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">{t('website.design.theme.useDefault')}</SelectItem>
            {headingFonts.map((f) => (
              <SelectItem key={f.key} value={f.key}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <label className="design-panel__field">
        {t('website.design.theme.bodyFont')}
        <Select
          value={theme.bodyFont ? theme.bodyFont : '__default__'}
          onValueChange={(v) =>
            setTheme((s) => {
              const { bodyFont: _drop, ...rest } = s;
              return v && v !== '__default__' ? { ...rest, bodyFont: v } : rest;
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">{t('website.design.theme.useDefault')}</SelectItem>
            {bodyFonts.map((f) => (
              <SelectItem key={f.key} value={f.key}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <div className="design-panel__field design-panel__field--hero">
        <span>{t('website.design.theme.heroImage')}</span>
        <ImageInput
          value={theme.heroImageKey ?? ''}
          cropAspect={16 / 9}
          fallbackURL={design.default_hero_url}
          onChange={(key) =>
            setTheme((s) => {
              const { heroImageKey: _drop, ...rest } = s;
              return key ? { ...rest, heroImageKey: key } : rest;
            })
          }
        />
      </div>

      <div className="design-panel__actions">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => setTheme({})}
        >
          {t('website.design.theme.reset')}
        </button>
      </div>
      <SaveFooter save={save} />
    </section>
  );
}

// --- Partners editor -------------------------------------------------

// PartnersPanel edits site.seed_content.sponsors — the partner cards
// rendered in the sponsors module. Each partner has a logo (required),
// an optional website URL, and a Primary flag that surfaces the
// partner in the site header strip and renders them larger on the
// dedicated /partners page. Rendered as a table: logo / URL / primary
// checkbox / reorder buttons / remove.
function PartnersPanel({
  design,
  onSave,
}: {
  design: WebsiteDesign;
  onSave: (partners: Partner[]) => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const [partners, setPartners] = useState<Partner[]>(design.partners);
  const save = useManualSave(partners, onSave, design.partners);

  const update = (idx: number, patch: Partial<Partner>) => {
    setPartners((curr) => curr.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const move = (idx: number, dir: -1 | 1) => {
    setPartners((curr) => {
      const next = curr.slice();
      const dest = idx + dir;
      if (dest < 0 || dest >= next.length) return curr;
      const a = next[idx]!;
      const b = next[dest]!;
      next[idx] = b;
      next[dest] = a;
      return next;
    });
  };
  const remove = (idx: number) =>
    setPartners((curr) => curr.filter((_, i) => i !== idx));
  const add = () =>
    setPartners((curr) => [...curr, { logo: '' }]);

  return (
    <section className="design-panel">
      <header className="design-panel__head">
        <h2>{t('website.design.partners.heading')}</h2>
        <AutosaveStatusBadge status={save.status} error={save.error} />
      </header>
      <p>{t('website.design.partners.body')}</p>

      {partners.length === 0 ? (
        <p className="design-panel__empty">{t('website.design.partners.empty')}</p>
      ) : (
        <table className="design-panel__partner-table">
          <thead>
            <tr>
              <th scope="col">{t('website.design.partners.logoLabel')}</th>
              <th scope="col">{t('website.design.partners.urlLabel')}</th>
              <th scope="col" className="design-panel__partner-table__col--primary">{t('website.design.partners.primaryLabel')}</th>
              <th scope="col" className="design-panel__partner-table__col--actions">
                <span className="sr-only">{t('common.actions', { defaultValue: 'Actions' })}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {partners.map((p, idx) => (
              <tr key={idx}>
                <td>
                  <ImageInput
                    value={p.logo}
                    variant="compact"
                    hideRemove
                    onChange={(key) => update(idx, { logo: key })}
                  />
                </td>
                <td>
                  <input
                    type="url"
                    className="design-panel__partner-table__url"
                    value={p.url ?? ''}
                    maxLength={500}
                    placeholder="https://…"
                    onChange={(e) => update(idx, { url: e.target.value })}
                  />
                </td>
                <td className="design-panel__partner-table__col--primary">
                  <label className="design-panel__partner-table__primary">
                    <input
                      type="checkbox"
                      checked={p.primary === true}
                      onChange={(e) => update(idx, { primary: e.target.checked })}
                    />
                    <span className="sr-only">{t('website.design.partners.primaryLabel')}</span>
                  </label>
                </td>
                <td className="design-panel__partner-table__col--actions">
                  <div className="design-panel__partner-actions">
                    <button
                      type="button"
                      className="design-panel__partner-move"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      aria-label={t('common.up')}
                    >↑</button>
                    <button
                      type="button"
                      className="design-panel__partner-move"
                      onClick={() => move(idx, 1)}
                      disabled={idx === partners.length - 1}
                      aria-label={t('common.down')}
                    >↓</button>
                    <button
                      type="button"
                      className="design-panel__partner-remove"
                      onClick={() => remove(idx)}
                      aria-label={t('website.design.partners.removeCta')}
                    >×</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="design-panel__actions">
        <button type="button" className="btn btn--ghost" onClick={add}>
          {t('website.design.partners.add')}
        </button>
      </div>
      <SaveFooter save={save} />
    </section>
  );
}

// --- Translations tab ------------------------------------------------

// TranslationsPanel edits the per-language variants of the site's free-text
// page copy — the about text and the events list. The source copy (shown
// read-only) is what visitors see in the site's source language; each other
// supported language gets an editable variant. Blank fields fall back to the
// source at render time, so a partial translation is fine. Events translate
// as a complete set: a language's translated events only publish once every
// event has a translated title (matching the renderer, which swaps the whole
// events list when a translation provides one).
function TranslationsPanel({
  design,
  onSave,
}: {
  design: WebsiteDesign;
  onSave: (
    sourceLanguage: string,
    translations: Record<string, SeedContentTranslation>,
  ) => Promise<void>;
}) {
  const { t } = useTranslation('common');

  // The language the canonical about/events are written in. Defaults to the
  // site's primary language when seed_content didn't record one.
  const sourceLang = design.content_source_language || design.primary_language;
  const sourceEvents = useMemo(() => design.events ?? [], [design.events]);

  // Target languages = every supported language except the source.
  const targetLangs = useMemo(
    () => design.supported_languages.filter((c) => c !== sourceLang),
    [design.supported_languages, sourceLang],
  );

  type EventDraft = { title: string; summary: string; location: string };
  type LangDraft = { about: string; events: EventDraft[] };

  // Draft state mirrors the loaded translations: each event row tracks a
  // source event by index; the shared date is re-attached on save.
  const initialDrafts = useMemo<Record<string, LangDraft>>(() => {
    const out: Record<string, LangDraft> = {};
    for (const code of targetLangs) {
      const tr = design.content_translations[code];
      out[code] = {
        about: tr?.about ?? '',
        events: sourceEvents.map((_, i) => {
          const e = tr?.events?.[i];
          return {
            title: e?.title ?? '',
            summary: e?.summary ?? '',
            location: e?.location ?? '',
          };
        }),
      };
    }
    return out;
  }, [targetLangs, design.content_translations, sourceEvents]);

  const [drafts, setDrafts] = useState<Record<string, LangDraft>>(initialDrafts);
  useEffect(() => setDrafts(initialDrafts), [initialDrafts]);

  // Canonicalize drafts into the API shape: trim, attach source dates, include
  // a language's events only when every event has a translated title (else
  // they fall back to source wholesale), and drop a language whose translation
  // ends up empty so the server's non-empty rule holds.
  const buildTranslations = useCallback(
    (state: Record<string, LangDraft>): Record<string, SeedContentTranslation> => {
      const out: Record<string, SeedContentTranslation> = {};
      for (const code of targetLangs) {
        const d = state[code];
        if (!d) continue;
        const about = d.about.trim();
        const allTitled =
          sourceEvents.length > 0 && d.events.every((e) => e.title.trim() !== '');
        let events: SeedEvent[] | undefined;
        if (allTitled) {
          events = d.events.map((e, i) => {
            const ev: SeedEvent = {
              title: e.title.trim(),
              summary: e.summary.trim() || sourceEvents[i]?.summary || '',
              location: e.location.trim() || sourceEvents[i]?.location || '',
            };
            // Date is shared with the source and not language-specific; only
            // attach it when the source event carries one.
            const date = sourceEvents[i]?.date;
            if (date) ev.date = date;
            return ev;
          });
        }
        if (about === '' && !events) continue;
        const entry: SeedContentTranslation = {};
        if (about !== '') entry.about = about;
        if (events) entry.events = events;
        out[code] = entry;
      }
      return out;
    },
    [targetLangs, sourceEvents],
  );

  const value = useMemo(() => buildTranslations(drafts), [buildTranslations, drafts]);
  const initial = useMemo(
    () => buildTranslations(initialDrafts),
    [buildTranslations, initialDrafts],
  );
  const save = useManualSave(value, (v) => onSave(sourceLang, v), initial);

  const langName = (code: string) =>
    PLATFORM_LANGUAGES.find((l) => l.code === code)?.endonym ?? code.toUpperCase();

  const updateAbout = (code: string, about: string) =>
    setDrafts((curr) => ({ ...curr, [code]: { ...curr[code]!, about } }));
  const updateEvent = (code: string, idx: number, patch: Partial<EventDraft>) =>
    setDrafts((curr) => ({
      ...curr,
      [code]: {
        ...curr[code]!,
        events: curr[code]!.events.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
      },
    }));

  return (
    <section className="design-panel">
      <header className="design-panel__head">
        <h2>{t('website.design.translations.heading')}</h2>
        <AutosaveStatusBadge status={save.status} error={save.error} />
      </header>
      <p>{t('website.design.translations.body')}</p>

      <div className="design-panel__translations-source">
        <h3>{t('website.design.translations.sourceHeading', { lang: langName(sourceLang) })}</h3>
        <p className="design-panel__translations-source-about">
          {design.about.trim() || t('website.design.translations.noAbout')}
        </p>
        {sourceEvents.length > 0 && (
          <ul className="design-panel__translations-source-events">
            {sourceEvents.map((e, i) => (
              <li key={i}>{e.title}</li>
            ))}
          </ul>
        )}
      </div>

      {targetLangs.map((code) => {
        const d = drafts[code]!;
        const incomplete =
          sourceEvents.length > 0 &&
          d.events.some((e) => e.title.trim() !== '') &&
          !d.events.every((e) => e.title.trim() !== '');
        return (
          <fieldset key={code} className="design-panel__translations-lang">
            <legend lang={code}>{langName(code)}</legend>
            <label className="design-panel__field">
              <span className="design-panel__field-label">
                {t('website.design.translations.aboutLabel')}
              </span>
              <textarea
                className="design-panel__textarea"
                rows={4}
                value={d.about}
                maxLength={8000}
                placeholder={design.about.trim()}
                onChange={(e) => updateAbout(code, e.target.value)}
              />
            </label>
            {sourceEvents.length > 0 && (
              <div className="design-panel__translations-events">
                <h4>{t('website.design.translations.eventsHeading')}</h4>
                {incomplete && (
                  <p className="design-panel__hint">
                    {t('website.design.translations.eventsHint')}
                  </p>
                )}
                {sourceEvents.map((src, i) => (
                  <div key={i} className="design-panel__translations-event">
                    <Input
                      type="text"
                      value={d.events[i]?.title ?? ''}
                      placeholder={src.title}
                      aria-label={t('website.design.translations.eventTitle')}
                      onChange={(e) => updateEvent(code, i, { title: e.target.value })}
                    />
                    <Input
                      type="text"
                      value={d.events[i]?.summary ?? ''}
                      placeholder={src.summary || ''}
                      aria-label={t('website.design.translations.eventSummary')}
                      onChange={(e) => updateEvent(code, i, { summary: e.target.value })}
                    />
                    <Input
                      type="text"
                      value={d.events[i]?.location ?? ''}
                      placeholder={src.location || ''}
                      aria-label={t('website.design.translations.eventLocation')}
                      onChange={(e) => updateEvent(code, i, { location: e.target.value })}
                    />
                  </div>
                ))}
              </div>
            )}
          </fieldset>
        );
      })}
      <SaveFooter save={save} />
    </section>
  );
}

// --- History tab -----------------------------------------------------

function HistoryPanel({
  token,
  slug,
  websiteId,
  onAfterRevert,
}: {
  token: string | null;
  slug: string;
  websiteId: string;
  onAfterRevert: () => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const [versions, setVersions] = useState<WebsiteVersion[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [err, setErr] = useState<string | null>(null);
  const [busyVersion, setBusyVersion] = useState<number | null>(null);
  // previewVersion drives the modal — undefined means closed, a number
  // means "open the modal and fetch this version's snapshot". The
  // detail (snapshot body) loads lazily inside the modal so the list
  // load stays cheap.
  const [previewVersion, setPreviewVersion] = useState<number | null>(null);
  const confirm = useConfirm();

  // latestVersion tracks the absolute newest version across all pages
  // so the "Current" label stays correct when the user is browsing
  // page 2+ (where the page's row 0 isn't the latest). Page 1 row 0
  // is the source of truth.
  const [latestVersion, setLatestVersion] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await listWebsiteVersions(token, slug, websiteId, {
        offset: (page - 1) * size,
        limit: size,
      });
      setVersions(res.versions);
      setTotal(res.total);
      if (page === 1 && res.versions.length > 0) {
        setLatestVersion(res.versions[0]!.version);
      }
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, slug, websiteId, page, size]);

  useEffect(() => {
    void load();
  }, [load]);

  const doRevert = async (version: number, summary: string) => {
    if (
      !(await confirm({
        title: t('website.design.history.revertTitle'),
        body: t('website.design.history.revertBody', { version, summary }),
        confirmLabel: t('website.design.history.revertCta'),
        tone: 'destructive',
      }))
    ) {
      return;
    }
    setBusyVersion(version);
    try {
      await revertWebsiteVersion(token, slug, websiteId, version);
      setPreviewVersion(null);
      // A revert creates a new version, so jump back to page 1 to show
      // it; if we're already on page 1, reload picks it up.
      if (page !== 1) {
        setPage(1);
      } else {
        await load();
      }
      await onAfterRevert();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyVersion(null);
    }
  };

  return (
    <section className="design-panel">
      <h2>{t('website.design.history.heading')}</h2>
      <p>{t('website.design.history.body')}</p>

      {err && <p className="design-panel__error">{err}</p>}

      {versions === null ? (
        <p className="design-panel__empty">{t('common.loading')}</p>
      ) : total === 0 ? (
        <p className="design-panel__empty">{t('website.design.history.empty')}</p>
      ) : (
        <div className="design-panel__history-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">{t('website.design.history.col.version')}</TableHead>
                <TableHead>{t('website.design.history.col.summary')}</TableHead>
                <TableHead className="w-32">{t('website.design.history.col.actor')}</TableHead>
                <TableHead className="w-32">{t('website.design.history.col.time')}</TableHead>
                <TableHead className="w-48 text-right">{t('website.design.history.col.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((v) => {
                const isLatest = latestVersion !== null && v.version === latestVersion;
                return (
                  <TableRow key={v.version}>
                    <TableCell className="font-mono text-xs text-muted-foreground">v{v.version}</TableCell>
                    <TableCell>
                      <span>{v.summary || t('website.design.history.defaultSummary')}</span>
                      {v.reverted_from !== undefined && (
                        <small className="ml-2 text-muted-foreground">
                          · {t('website.design.history.revertedFrom', { version: v.reverted_from })}
                        </small>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`design-panel__history-actor design-panel__history-actor--${v.actor_type}`}>
                        {t(`website.design.history.actor.${v.actor_type}`)}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <time dateTime={v.created_at}>{formatRelative(v.created_at)}</time>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-2">
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => setPreviewVersion(v.version)}
                        >
                          {t('website.design.history.previewCta')}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          disabled={busyVersion !== null || isLatest}
                          onClick={() => void doRevert(v.version, v.summary)}
                          aria-label={t('website.design.history.revertCta')}
                        >
                          {isLatest
                            ? t('website.design.history.currentLabel')
                            : busyVersion === v.version
                              ? t('common.submitting')
                              : t('website.design.history.revertCta')}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="design-panel__history-pagination">
            <TablePagination
              total={total}
              page={page}
              size={size}
              pageSizes={[10, 25, 50]}
              onPageChange={setPage}
              onSizeChange={(next) => {
                setSize(next);
                setPage(1);
              }}
            />
          </div>
        </div>
      )}

      {previewVersion !== null && (
        <VersionPreviewModal
          token={token}
          slug={slug}
          websiteId={websiteId}
          version={previewVersion}
          canRevert={latestVersion !== null && previewVersion !== latestVersion}
          onClose={() => setPreviewVersion(null)}
          onRevert={(version, summary) => doRevert(version, summary)}
        />
      )}
    </section>
  );
}

// VersionPreviewModal fetches one version on mount and renders its
// snapshot as a readable summary — status, design-mode flag, nav, page
// list, theme overrides — rather than raw JSON. The Revert button at
// the bottom delegates to the parent panel; the parent owns the
// confirm + reload flow.
function VersionPreviewModal({
  token,
  slug,
  websiteId,
  version,
  canRevert,
  onClose,
  onRevert,
}: {
  token: string | null;
  slug: string;
  websiteId: string;
  version: number;
  canRevert: boolean;
  onClose: () => void;
  onRevert: (version: number, summary: string) => Promise<void> | void;
}) {
  const { t } = useTranslation('common');
  const [detail, setDetail] = useState<WebsiteVersionDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await getWebsiteVersion(token, slug, websiteId, version);
        if (alive) setDetail(d);
      } catch (e) {
        if (alive) setErr((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, slug, websiteId, version]);

  // Native <dialog> handles ESC + focus trap. showModal once on mount.
  useEffect(() => {
    const d = dialogRef.current;
    if (d && !d.open) d.showModal();
  }, []);

  const snap = detail?.snapshot;

  return (
    <dialog
      ref={dialogRef}
      className="version-preview-modal"
      onClose={onClose}
      onClick={(e) => {
        // Close on backdrop click — clicking inside the dialog content
        // stops propagation via the inner wrapper below.
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className="version-preview-modal__inner" onClick={(e) => e.stopPropagation()}>
        <header className="version-preview-modal__head">
          <h3>
            {t('website.design.history.previewTitle', { version })}
          </h3>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            aria-label={t('common.cancel')}
          >
            ×
          </button>
        </header>

        {err && <p className="design-panel__error">{err}</p>}
        {!detail && !err && (
          <p className="design-panel__empty">{t('common.loading')}</p>
        )}

        {snap && detail && (
          <div className="version-preview-modal__body">
            <p className="version-preview-modal__summary">{detail.summary}</p>

            <dl className="version-preview-modal__fields">
              <div>
                <dt>{t('website.design.history.field.status')}</dt>
                <dd>{snap.status}</dd>
              </div>
              <div>
                <dt>{t('website.design.history.field.designMode')}</dt>
                <dd>
                  {snap.design_mode_enabled
                    ? t('website.design.history.field.designOn')
                    : t('website.design.history.field.designOff')}
                </dd>
              </div>
              <div>
                <dt>{t('website.design.history.field.template')}</dt>
                <dd>{snap.template}</dd>
              </div>
              <div>
                <dt>{t('website.design.history.field.modules')}</dt>
                <dd>
                  {snap.enabled_modules.length === 0
                    ? '—'
                    : snap.enabled_modules.join(', ')}
                </dd>
              </div>
              <div>
                <dt>{t('website.design.history.field.nav')}</dt>
                <dd>
                  {snap.site_entries.length === 0
                    ? t('website.design.history.field.navAuto')
                    : snap.site_entries
                        .filter((e) => e.show_in_nav)
                        .map((e) => e.label)
                        .join(' · ') || t('website.design.history.field.navHidden')}
                </dd>
              </div>
              <div>
                <dt>{t('website.design.history.field.pages')}</dt>
                <dd>
                  {(() => {
                    const pages = snap.site_entries.filter(
                      (e) => e.kind === 'page' && e.page,
                    );
                    if (pages.length === 0) return '—';
                    return pages
                      .map((e) => `${e.page!.title} (/${e.page!.slug})`)
                      .join(', ');
                  })()}
                </dd>
              </div>
              <div>
                <dt>{t('website.design.history.field.theme')}</dt>
                <dd>
                  <ThemePreview theme={snap.theme_overrides} />
                </dd>
              </div>
            </dl>
          </div>
        )}

        <footer className="version-preview-modal__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {canRevert && (
            <button
              type="button"
              className="btn btn--primary"
              disabled={!detail}
              onClick={() => {
                if (detail) void onRevert(detail.version, detail.summary);
              }}
            >
              {t('website.design.history.revertToCta', { version })}
            </button>
          )}
        </footer>
      </div>
    </dialog>
  );
}

// ThemePreview renders the override layer inline — color swatches +
// font + hero image — so the modal shows the visible diff without
// asking the user to read JSON.
function ThemePreview({ theme }: { theme: ThemeOverrides }) {
  const { t } = useTranslation('common');
  const empty =
    !theme.primaryColor &&
    !theme.accentColor &&
    !theme.headingFont &&
    !theme.bodyFont &&
    !theme.heroImageKey;
  if (empty) {
    return <span>{t('website.design.history.field.themeDefault')}</span>;
  }
  return (
    <ul className="version-preview-modal__theme">
      {theme.primaryColor && (
        <li>
          <span className="version-preview-modal__swatch" style={{ background: theme.primaryColor }} />
          {t('website.design.theme.primaryColor')}: {theme.primaryColor}
        </li>
      )}
      {theme.accentColor && (
        <li>
          <span className="version-preview-modal__swatch" style={{ background: theme.accentColor }} />
          {t('website.design.theme.accentColor')}: {theme.accentColor}
        </li>
      )}
      {theme.headingFont && (
        <li>{t('website.design.theme.headingFont')}: {theme.headingFont}</li>
      )}
      {theme.bodyFont && (
        <li>{t('website.design.theme.bodyFont')}: {theme.bodyFont}</li>
      )}
      {theme.heroImageKey && (
        <li>{t('website.design.theme.heroImage')}: {theme.heroImageKey}</li>
      )}
    </ul>
  );
}

// formatRelative renders an ISO timestamp as a coarse human duration
// (just now / 4m ago / 2h ago / 3d ago / 2026-05-19). Avoids pulling
// in a date library for what's essentially a single label.
function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d ago`;
  return new Date(iso).toISOString().slice(0, 10);
}

// --- General (slug rename) -------------------------------------------

function slugifyFE(input: string): string {
  return input
    .toLowerCase()
    .replace(/å/g, 'a').replace(/ä/g, 'a').replace(/ö/g, 'o')
    .replace(/ø/g, 'o').replace(/æ/g, 'ae').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function GeneralPanel({
  design,
  onSaveSlug,
}: {
  design: WebsiteDesign;
  onSaveSlug: (newSlug: string) => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const [slug, setSlug] = useState(design.slug);
  useEffect(() => { setSlug(design.slug); }, [design.slug]);
  const save = useManualSave(slug, onSaveSlug, design.slug);
  return (
    <section className="design-panel">
      <header className="design-panel__head">
        <h2>{t('website.design.general.heading')}</h2>
        <AutosaveStatusBadge status={save.status} error={save.error} />
      </header>
      <p>{t('website.design.general.body')}</p>
      <label className="design-panel__field">
        <span>{t('website.design.general.slugLabel')}</span>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(slugifyFE(e.target.value))}
          maxLength={80}
          inputMode="text"
          pattern="^[a-z0-9]+(-[a-z0-9]+)*$"
        />
      </label>
      <p className="design-panel__hint">{t('website.design.general.slugHint', { slug })}</p>
      <SaveFooter save={save} />
    </section>
  );
}

// --- Teams allowlist -------------------------------------------------

function TeamsPanel({
  token,
  slug,
  design,
  onSave,
}: {
  token: string | null;
  slug: string;
  design: WebsiteDesign;
  onSave: (teamIDs: string[]) => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const confirm = useConfirm();
  const toast = useToast();
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string[]>(() => [...design.team_ids]);

  useEffect(() => { setSelected([...design.team_ids]); }, [design.team_ids]);

  useEffect(() => {
    let cancelled = false;
    // Pull up to the pagination ceiling so the picker covers every team
    // without a second request — clubs rarely exceed a few dozen. We
    // load every team regardless of public_visibility; the picker tags
    // private ones with a badge and gates them behind a confirm that
    // flips public_visibility on add.
    void listTeams(token, slug, { limit: 200, sort: 'name', order: 'asc' }).then((res) => {
      if (cancelled) return;
      setAllTeams(res.teams);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [token, slug]);

  // useManualSave compares with ===, so feed it a sorted, stringified view.
  const value = useMemo(() => JSON.stringify([...selected].sort()), [selected]);
  const initial = useMemo(() => JSON.stringify([...design.team_ids].sort()), [design.team_ids]);
  const save = useManualSave(value, async () => { await onSave(selected); }, initial);

  const byID = useMemo(() => new Map(allTeams.map((tm) => [tm.id, tm])), [allTeams]);
  const selectedTeams = useMemo(
    () => selected.map((id) => byID.get(id) ?? { id, name: id, public_visibility: true } as Team),
    [selected, byID],
  );

  const add = useCallback(
    (id: string) => setSelected((prev) => (prev.includes(id) ? prev : [...prev, id])),
    [],
  );
  const remove = (id: string) => setSelected((prev) => prev.filter((x) => x !== id));

  // tryAdd is the picker's entry point. For a public team it just adds.
  // For a private team it surfaces a confirm dialog explaining that
  // selecting it will flip the team's public_visibility — adding the
  // team without that flip would leave the website allowlist pointing
  // at a team the renderer refuses to serve, so the flip is the only
  // useful path. updateTeam is fired immediately on confirm (same
  // pattern as the per-team toggle elsewhere); on success we mirror
  // the change into local state so the badge disappears.
  const tryAdd = useCallback(
    async (team: Team) => {
      if (team.public_visibility) {
        add(team.id);
        return;
      }
      const ok = await confirm({
        title: t('website.design.teams.confirmMakePublic.title'),
        body: t('website.design.teams.confirmMakePublic.body', { name: team.name }),
        confirmLabel: t('website.design.teams.confirmMakePublic.confirm'),
      });
      if (!ok) return;
      try {
        const updated = await updateTeam(token, slug, team.id, {
          name: team.name,
          description: team.description,
          color: team.color,
          public_visibility: true,
        });
        setAllTeams((prev) => prev.map((tm) => (tm.id === team.id ? updated : tm)));
        add(team.id);
      } catch {
        toast.error(t('website.design.teams.makePublicFailed'));
      }
    },
    [add, confirm, slug, t, toast, token],
  );

  return (
    <section className="design-panel">
      <header className="design-panel__head">
        <h2>{t('website.design.teams.heading')}</h2>
        <AutosaveStatusBadge status={save.status} error={save.error} />
      </header>
      <p>{t('website.design.teams.body')}</p>

      {loading ? (
        <p className="design-panel__hint">{t('common.loading')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Selected teams as removable chips. */}
          {selectedTeams.length === 0 ? (
            <p className="design-panel__hint">{t('website.design.teams.noneSelected', { defaultValue: 'No teams selected yet — add one below.' })}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedTeams.map((tm) => {
                // Parenthesised label kept in a variable so the parens aren't
                // bare string literals in JSX (react/jsx-no-literals).
                const privateLabel = `(${t('website.design.teams.privateBadge')})`;
                return (
                  <Badge key={tm.id} variant="secondary" className="gap-1.5 py-1 pl-3 pr-1.5 text-sm">
                    <span className="truncate">
                      {tm.name}
                      {!tm.public_visibility && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {privateLabel}
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => remove(tm.id)}
                      aria-label={t('website.design.teams.removeTeam', { defaultValue: 'Remove team', name: tm.name })}
                      className="rounded-pill p-0.5 text-muted-foreground transition-colors hover:bg-background/60 hover:text-destructive"
                    >
                      <X weight="bold" className="h-3.5 w-3.5" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}

          {/* Searchable add-team dropdown over the full team list,
              excluding teams already selected. Private teams are
              tagged with a badge and gated by a confirm in tryAdd. */}
          <AddTeamPicker
            teams={allTeams.filter((tm) => !selected.includes(tm.id))}
            onAdd={tryAdd}
          />

          <p className="design-panel__hint text-xs">
            {t('website.design.teams.privateAddHint')}
          </p>
        </div>
      )}

      <SaveFooter save={save} />
    </section>
  );
}

// AddTeamPicker — click-to-open searchable dropdown that adds one team per
// selection (the dropdown stays open so several teams can be added in a
// row). Mirrors the schedule TeamPicker's hand-rolled popover but for the
// add-to-list interaction. Client-side filtered over the passed pool.
function AddTeamPicker({
  teams,
  onAdd,
}: {
  teams: Team[];
  onAdd: (team: Team) => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else setSearch('');
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? teams.filter((tm) => tm.name.toLowerCase().includes(q)) : teams;
  }, [teams, search]);

  return (
    <div ref={wrapRef} className="relative">
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((v) => !v)}
        disabled={teams.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full justify-between"
      >
        <span className="flex items-center gap-2">
          <Plus weight="bold" className="h-4 w-4" />
          {teams.length === 0
            ? t('website.design.teams.allAdded', { defaultValue: 'All teams added' })
            : t('website.design.teams.addTeam', { defaultValue: 'Add a team' })}
        </span>
        <CaretDown weight="bold" className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Button>
      {open && teams.length > 0 && (
        <div
          role="listbox"
          className="absolute left-0 right-0 z-50 mt-1 flex max-h-72 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-lg"
        >
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <MagnifyingGlass weight="bold" className="h-4 w-4 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('website.design.teams.searchPlaceholder', { defaultValue: 'Search teams…' })}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {t('website.design.teams.searchEmpty', { defaultValue: 'No matching teams.' })}
              </p>
            ) : (
              filtered.map((tm) => (
                <button
                  key={tm.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => {
                    onAdd(tm);
                    setSearch('');
                    inputRef.current?.focus();
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted',
                  )}
                >
                  <span className="flex-1 truncate">{tm.name}</span>
                  {!tm.public_visibility && (
                    <Badge
                      variant="outline"
                      className="shrink-0 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
                    >
                      {t('website.design.teams.privateBadge')}
                    </Badge>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Custom domain ---------------------------------------------------

// DomainPanel lets an admin connect their club's own domain (www.mff.se) to
// this website variant. Flow: type the hostname → Connect (we detect the DNS
// provider + show the exact record to add) → add the record at the provider
// → Check DNS until it verifies. TLS / serving the site on the domain is a
// later slice; "verified" here means the DNS points at us.
function DomainPanel({
  token,
  slug,
  websiteId,
}: {
  token: string | null;
  slug: string;
  websiteId: string;
}) {
  const { t } = useTranslation('common');
  const confirm = useConfirm();
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [hostname, setHostname] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyID, setBusyID] = useState<string | null>(null);
  // verifyingIDs covers every in-flight DNS check — manual ("Check now"),
  // the immediate-on-connect kick, and each 30s auto-poll tick. The row
  // shows the "searching" spinner while it's set so the user always knows
  // when we're actually hitting the resolver.
  const [verifyingIDs, setVerifyingIDs] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let cancelled = false;
    void listCustomDomains(token, slug, websiteId)
      .then((res) => { if (!cancelled) setDomains(res.domains); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, slug, websiteId]);

  const markVerifying = useCallback((id: string, on: boolean) => {
    setVerifyingIDs((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const runVerify = useCallback(async (id: string) => {
    markVerifying(id, true);
    try {
      const { domain } = await verifyCustomDomain(token, slug, websiteId, id);
      setDomains((prev) => prev.map((d) => (d.id === id ? domain : d)));
    } catch {
      // Transient resolver hiccup — next tick / next click picks it up.
    } finally {
      markVerifying(id, false);
    }
  }, [markVerifying, slug, token, websiteId]);

  // Auto-poll pending domains every 30s so the user doesn't have to
  // click "Check now" after every DNS edit. The worker also polls
  // server-side every minute, but the SPA poll keeps the UI in lockstep
  // — when the worker flips status to verified, the next SPA tick
  // reflects it within 30s. Stops as soon as there are no pending rows
  // (verified rows only get re-checked daily by the worker).
  useEffect(() => {
    const pendingIDs = domains.filter((d) => d.status === 'pending').map((d) => d.id);
    if (pendingIDs.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      for (const id of pendingIDs) {
        if (cancelled) return;
        await runVerify(id);
      }
    };
    const interval = window.setInterval(() => { void tick(); }, 30_000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [domains, runVerify]);

  const connect = async () => {
    const h = hostname.trim().toLowerCase();
    if (!h) return;
    setConnecting(true);
    setError(null);
    try {
      const created = await connectCustomDomain(token, slug, websiteId, h);
      setDomains((prev) => [created, ...prev]);
      setHostname('');
      // Kick an immediate DNS check so the user sees activity right away
      // instead of waiting 30s for the first auto-poll tick. If the user
      // already added the records before clicking Connect this often
      // flips to verified within a second.
      if (created.status !== 'verified') {
        void runVerify(created.id);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'unknown');
    } finally {
      setConnecting(false);
    }
  };

  const verify = (id: string) => { void runVerify(id); };

  const disconnect = async (d: CustomDomain) => {
    if (!(await confirm({
      title: t('website.design.domain.disconnectTitle', { defaultValue: 'Disconnect domain?' }),
      body: t('website.design.domain.disconnectBody', { hostname: d.hostname, defaultValue: `${d.hostname} will stop pointing at this website.` }),
      confirmLabel: t('website.design.domain.disconnectCta', { defaultValue: 'Disconnect' }),
      tone: 'destructive',
    }))) return;
    setBusyID(d.id);
    try {
      await disconnectCustomDomain(token, slug, websiteId, d.id);
      setDomains((prev) => prev.filter((x) => x.id !== d.id));
    } finally {
      setBusyID(null);
    }
  };

  return (
    <section className="design-panel">
      <header className="design-panel__head">
        <h2>{t('website.design.domain.heading', { defaultValue: 'Custom domain' })}</h2>
      </header>
      <p>{t('website.design.domain.body', { defaultValue: 'Point your own web address (for example www.mff.se) at this website. Add the domain below, then add the DNS record we show you at your domain provider.' })}</p>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="flex-1">
          <Input
            type="text"
            inputMode="url"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void connect(); }}
            placeholder={t('website.design.domain.placeholder', { defaultValue: 'www.yourclub.se' })}
            disabled={connecting}
          />
          {error && (
            <p className="mt-1 text-xs text-destructive" role="alert">
              {t(`website.design.domain.error.${error}`, { defaultValue: t('website.design.domain.error.unknown', { defaultValue: 'Could not connect that domain.' }) })}
            </p>
          )}
        </div>
        <Button type="button" onClick={() => void connect()} disabled={connecting || !hostname.trim()}>
          {connecting ? t('common.submitting') : t('website.design.domain.connect', { defaultValue: 'Connect domain' })}
        </Button>
      </div>

      {loading ? (
        <p className="design-panel__hint">{t('common.loading')}</p>
      ) : domains.length === 0 ? (
        <p className="design-panel__hint">{t('website.design.domain.empty', { defaultValue: 'No custom domains connected yet.' })}</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-4">
          {domains.map((d) => (
            <li key={d.id} className="rounded-xl border border-border bg-background p-4">
              <div className="flex items-start justify-between gap-2">
                <span className="font-heading font-semibold">{d.hostname}</span>
                <Badge variant={d.status === 'verified' ? 'default' : 'outline'}>
                  {d.status === 'verified'
                    ? t('website.design.domain.statusVerified', { defaultValue: 'Verified' })
                    : t('website.design.domain.statusPending', { defaultValue: 'Pending DNS' })}
                </Badge>
              </div>

              {d.status !== 'verified' && (
                <div className="mt-3 flex flex-col gap-3">
                  <p className="text-sm text-muted-foreground">
                    {d.dns_provider_name
                      ? t('website.design.domain.providerDetected', { provider: d.dns_provider_name, defaultValue: `We detected your DNS provider: ${d.dns_provider_name}. Add the record below — we'll detect the change automatically.` })
                      : t('website.design.domain.providerUnknown', { defaultValue: "Add the DNS record below at your domain provider — we'll detect the change automatically." })}
                  </p>
                  {d.records.map((rec, i) => (
                    <DomainRecordCard key={i} record={rec} />
                  ))}
                  {d.dns_help_url && (
                    <a
                      href={d.dns_help_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-foreground underline underline-offset-4 hover:text-foreground/80"
                    >
                      {t('website.design.domain.helpLink', { provider: d.dns_provider_name ?? '', defaultValue: `How to add a DNS record${d.dns_provider_name ? ` at ${d.dns_provider_name}` : ''}` })}
                      <ArrowSquareOut weight="bold" className="h-3.5 w-3.5" aria-hidden />
                    </a>
                  )}
                  <p className="flex items-center gap-2 text-xs text-muted-foreground">
                    {verifyingIDs.has(d.id) ? (
                      <>
                        <CircleNotch weight="bold" className="h-3.5 w-3.5 animate-spin" aria-hidden />
                        <span>{t('website.design.domain.searching', { defaultValue: 'Searching for your DNS record…' })}</span>
                      </>
                    ) : d.last_checked_at ? (
                      <span>{t('website.design.domain.autoCheckingSince', { time: formatRelative(d.last_checked_at), defaultValue: `Checking DNS automatically — last attempt ${formatRelative(d.last_checked_at)}.` })}</span>
                    ) : (
                      <span>{t('website.design.domain.autoCheckingFirst', { defaultValue: 'Checking DNS automatically…' })}</span>
                    )}
                  </p>
                </div>
              )}

              <div className="mt-4 flex items-center justify-end gap-2">
                {d.status !== 'verified' && (
                  <Button type="button" variant="outline" size="sm" onClick={() => verify(d.id)} disabled={verifyingIDs.has(d.id) || busyID === d.id}>
                    {verifyingIDs.has(d.id) ? t('common.loading') : t('website.design.domain.checkNow', { defaultValue: 'Check now' })}
                  </Button>
                )}
                <Button type="button" variant="outline" size="sm" onClick={() => void disconnect(d)} disabled={busyID === d.id} className="text-muted-foreground hover:text-destructive">
                  {t('website.design.domain.disconnect', { defaultValue: 'Disconnect' })}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// DomainRecordCard renders one DNS record with copy-to-clipboard fields so a
// volunteer can paste the exact values into their provider's form.
function DomainRecordCard({ record }: { record: RecommendedRecord }) {
  const { t } = useTranslation('common');
  const fields: Array<[string, string]> = [
    [t('website.design.domain.recordType', { defaultValue: 'Type' }), record.type],
    [t('website.design.domain.recordName', { defaultValue: 'Name' }), record.name],
    [t('website.design.domain.recordValue', { defaultValue: 'Value' }), record.value],
    [t('website.design.domain.recordTtl', { defaultValue: 'TTL' }), String(record.ttl)],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/30 p-3 sm:grid-cols-4">
      {fields.map(([label, val]) => (
        <div key={label} className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <button
            type="button"
            onClick={() => { void navigator.clipboard?.writeText(val); }}
            title={t('website.design.domain.copy', { defaultValue: 'Copy' })}
            className="block w-full truncate text-left font-mono text-sm hover:text-primary"
          >
            {val}
          </button>
        </div>
      ))}
    </div>
  );
}

// --- Featured tiles --------------------------------------------------

function FeaturedPanel({
  design,
  onSave,
}: {
  design: WebsiteDesign;
  onSave: (tiles: FeaturedTile[]) => Promise<void>;
}) {
  const { t } = useTranslation('common');
  const [tiles, setTiles] = useState<FeaturedTile[]>(() => design.featured_tiles ?? []);
  useEffect(() => { setTiles(design.featured_tiles ?? []); }, [design.featured_tiles]);

  // Stringify for stable equality — useManualSave compares with ===.
  const value = useMemo(() => JSON.stringify(tiles), [tiles]);
  const initial = useMemo(() => JSON.stringify(design.featured_tiles ?? []), [design.featured_tiles]);
  const save = useManualSave(value, async () => { await onSave(tiles); }, initial);

  const update = (i: number, patch: Partial<FeaturedTile>) => {
    setTiles((prev) => prev.map((t2, idx) => (idx === i ? { ...t2, ...patch } : t2)));
  };
  const add = () => {
    if (tiles.length >= MAX_FEATURED_TILES) return;
    setTiles((prev) => [...prev, { kind: 'team', label: '', ref: '' }]);
  };
  const remove = (i: number) => setTiles((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    setTiles((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i]!, next[j]!] = [next[j]!, next[i]!];
      return next;
    });
  };

  return (
    <section className="design-panel">
      <header className="design-panel__head">
        <h2>{t('website.design.featured.heading')}</h2>
        <AutosaveStatusBadge status={save.status} error={save.error} />
      </header>
      <p>{t('website.design.featured.body')}</p>
      <ol className="design-panel__list">
        {tiles.map((tile, i) => (
          <li key={i} className="design-panel__row">
            <div className="design-panel__row-grid">
              <div className="design-panel__field">
                <span>{t('website.design.featured.kind')}</span>
                <Select
                  value={tile.kind}
                  onValueChange={(v) => update(i, { kind: v as FeaturedTileKind })}
                >
                  <SelectTrigger aria-label={t('website.design.featured.kind')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEATURED_TILE_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {t(`website.design.featured.kindLabel.${k}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <label className="design-panel__field">
                <span>{t('website.design.featured.label')}</span>
                <input
                  type="text"
                  value={tile.label ?? ''}
                  onChange={(e) => update(i, { label: e.target.value })}
                  maxLength={80}
                />
              </label>
              <label className="design-panel__field">
                <span>{t('website.design.featured.ref')}</span>
                <input
                  type="text"
                  value={tile.ref ?? ''}
                  onChange={(e) => update(i, { ref: e.target.value })}
                  placeholder={t(`website.design.featured.refPlaceholder.${tile.kind}`)}
                />
              </label>
              <label className="design-panel__field">
                <span>{t('website.design.featured.imageKey')}</span>
                <input
                  type="text"
                  value={tile.image_object_key ?? ''}
                  onChange={(e) => update(i, { image_object_key: e.target.value })}
                  placeholder="websites/…"
                />
              </label>
            </div>
            <div className="design-panel__row-actions">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === tiles.length - 1}>↓</button>
              <button type="button" onClick={() => remove(i)}>
                {t('common.remove')}
              </button>
            </div>
          </li>
        ))}
      </ol>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={add}
        disabled={tiles.length >= MAX_FEATURED_TILES}
      >
        {t('website.design.featured.addCta')}
      </button>
      <SaveFooter save={save} />
    </section>
  );
}
